'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import MarketingLayout from '@/components/MarketingLayout';

export default function ROICalculatorPage() {
  const [flanges, setFlanges] = useState(40);
  const [crewSize, setCrewSize] = useState(4);
  const [laborRate, setLaborRate] = useState(175);
  const [downtimeCost, setDowntimeCost] = useState(20000);
  const [systemInvestment, setSystemInvestment] = useState(40000);

  const traditionalMinPerFlange = 45;
  const bridgeflowMinPerFlange = 8;

  const traditionalHours = (flanges * traditionalMinPerFlange) / 60;
  const bridgeflowHours = (flanges * bridgeflowMinPerFlange) / 60;

  const traditionalLabor = Math.round(crewSize * laborRate * traditionalHours);
  const bridgeflowLabor = Math.round(crewSize * laborRate * bridgeflowHours);

  const traditionalDowntime = Math.round(downtimeCost * traditionalHours);
  const bridgeflowDowntime = Math.round(downtimeCost * bridgeflowHours);

  const timeSaved = traditionalHours - bridgeflowHours;
  const laborSavings = traditionalLabor - bridgeflowLabor;
  const downtimeSavings = traditionalDowntime - bridgeflowDowntime;
  const totalSavings = laborSavings + downtimeSavings;
  const roi = Math.round(((totalSavings - systemInvestment) / systemInvestment) * 100);
  const paybackCycles = systemInvestment / totalSavings;

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const sliders = [
    { label: 'Critical Flanges in Scope', value: flanges, setter: setFlanges, min: 1, max: 200, step: 1, display: `${flanges} flanges` },
    { label: 'Bolting Crew Size', value: crewSize, setter: setCrewSize, min: 1, max: 20, step: 1, display: `${crewSize} persons` },
    { label: 'Labor Rate (fully burdened)', value: laborRate, setter: setLaborRate, min: 50, max: 500, step: 25, display: `${fmt(laborRate)}/hr/person` },
    { label: 'Facility Downtime Cost', value: downtimeCost, setter: setDowntimeCost, min: 1000, max: 500000, step: 1000, display: `${fmt(downtimeCost)}/hr` },
    { label: 'System Investment', value: systemInvestment, setter: setSystemInvestment, min: 10000, max: 500000, step: 5000, display: fmt(systemInvestment) },
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0F1A]" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(249,115,22,0.06) 0%, transparent 60%)',
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">ROI Calculator</span>
          <h1 className="text-display text-[#F1F5F9] mt-4 mb-4 max-w-3xl mx-auto">
            Calculate your shutdown savings
          </h1>
          <p className="text-[#64748B] text-lg max-w-xl mx-auto">
            Adjust the parameters below to model your specific operation. See exactly how much BridgeFlow™ MTE saves per shutdown event.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            {/* Inputs */}
            <div className="xl:col-span-5">
              <div className="rounded-2xl border border-[#1E293B] bg-[#111827] overflow-hidden sticky top-24">
                <div className="px-6 py-5 border-b border-[#1E293B] bg-[#0A0F1A]">
                  <h2 className="text-[#F1F5F9] font-bold text-lg">Scenario Parameters</h2>
                  <p className="text-[#64748B] text-sm mt-1">Adjust to match your operation</p>
                </div>
                <div className="p-6 space-y-6">
                  {sliders.map((s) => (
                    <div key={s.label}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-[#94A3B8] text-sm font-medium">{s.label}</label>
                        <span className="font-mono text-[#F97316] font-bold text-sm">{s.display}</span>
                      </div>
                      <input
                        type="range"
                        min={s.min}
                        max={s.max}
                        step={s.step}
                        value={s.value}
                        onChange={(e) => s.setter(Number(e.target.value))}
                        className="w-full accent-[#F97316] h-1.5 rounded-full cursor-pointer"
                        aria-label={s.label}
                      />
                      <div className="flex justify-between text-xs text-[#475569] mt-1">
                        <span>{s.min}</span>
                        <span>{s.max}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="xl:col-span-7 space-y-5">
              {/* Method comparison */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-[#EF4444]/20 bg-[#111827] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 rounded-full bg-[#EF4444]" />
                    <span className="text-[#EF4444] text-xs font-semibold uppercase tracking-wider">Traditional Method</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#64748B] text-sm">Total Time</span>
                      <span className="font-mono text-[#F1F5F9] font-bold">{traditionalHours.toFixed(1)}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B] text-sm">Labor Cost</span>
                      <span className="font-mono text-[#F1F5F9] font-bold">{fmt(traditionalLabor)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B] text-sm">Downtime Cost</span>
                      <span className="font-mono text-[#EF4444] font-bold">{fmt(traditionalDowntime)}</span>
                    </div>
                    <div className="pt-3 border-t border-[#1E293B] flex justify-between">
                      <span className="text-[#94A3B8] text-sm font-semibold">Total Event Cost</span>
                      <span className="font-mono text-[#EF4444] font-bold">{fmt(traditionalLabor + traditionalDowntime)}</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-[#22C55E]/20 bg-[#111827] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 rounded-full bg-[#22C55E]" />
                    <span className="text-[#22C55E] text-xs font-semibold uppercase tracking-wider">BridgeFlow™ MTE</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#64748B] text-sm">Total Time</span>
                      <span className="font-mono text-[#22C55E] font-bold">{bridgeflowHours.toFixed(1)}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B] text-sm">Labor Cost</span>
                      <span className="font-mono text-[#22C55E] font-bold">{fmt(bridgeflowLabor)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#64748B] text-sm">Downtime Cost</span>
                      <span className="font-mono text-[#22C55E] font-bold">{fmt(bridgeflowDowntime)}</span>
                    </div>
                    <div className="pt-3 border-t border-[#1E293B] flex justify-between">
                      <span className="text-[#94A3B8] text-sm font-semibold">Total Event Cost</span>
                      <span className="font-mono text-[#22C55E] font-bold">{fmt(bridgeflowLabor + bridgeflowDowntime)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Savings */}
              <div className="rounded-2xl border border-[#22C55E]/30 bg-gradient-to-br from-[#0a1a0f] to-[#111827] p-7">
                <p className="text-[#64748B] text-xs uppercase tracking-widest mb-2">Total Event Savings</p>
                <p className="font-mono text-5xl font-bold text-[#22C55E] mb-5">{fmt(totalSavings)}</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-xl bg-[#0A0F1A] border border-[#1E293B] p-4">
                    <p className="font-mono text-[#22C55E] font-bold text-xl">{timeSaved.toFixed(1)}h</p>
                    <p className="text-[#64748B] text-xs mt-1">Time Saved</p>
                  </div>
                  <div className="rounded-xl bg-[#0A0F1A] border border-[#1E293B] p-4">
                    <p className="font-mono text-[#22C55E] font-bold text-xl">{fmt(laborSavings)}</p>
                    <p className="text-[#64748B] text-xs mt-1">Labor Savings</p>
                  </div>
                  <div className="rounded-xl bg-[#0A0F1A] border border-[#1E293B] p-4">
                    <p className="font-mono text-[#22C55E] font-bold text-xl">{fmt(downtimeSavings)}</p>
                    <p className="text-[#64748B] text-xs mt-1">Downtime Recovery</p>
                  </div>
                </div>
              </div>

              {/* ROI */}
              <div className={`rounded-2xl border p-7 ${roi >= 0 ? 'border-[#F97316]/30 bg-gradient-to-br from-[#1a0f00] to-[#111827]' : 'border-[#EAB308]/30 bg-[#111827]'}`}>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-[#64748B] text-xs uppercase tracking-widest mb-2">First-Event ROI</p>
                    <p className={`font-mono text-5xl font-bold ${roi >= 0 ? 'text-[#F97316]' : 'text-[#EAB308]'}`}>
                      {roi >= 0 ? '+' : ''}{roi}%
                    </p>
                    <p className="text-[#64748B] text-sm mt-2">
                      {roi >= 100
                        ? 'Full payback in less than one shutdown cycle'
                        : roi >= 0
                        ? 'System pays back within first few shutdowns'
                        : 'Increase flanges or downtime cost to improve ROI'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#64748B] text-xs uppercase tracking-widest mb-2">Payback Period</p>
                    <p className={`font-mono text-5xl font-bold ${paybackCycles < 1 ? 'text-[#F97316]' : 'text-[#94A3B8]'}`}>
                      {paybackCycles.toFixed(2)}
                    </p>
                    <p className="text-[#64748B] text-sm mt-2">shutdown cycles to full payback</p>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <p className="text-[#475569] text-xs leading-relaxed">
                * Model calculates direct labor and downtime savings only. Excluded: avoided flange failures, fugitive emissions penalties, rework costs, reduced crew requirements, improved TRIR metrics, and confined space duration reduction. Actual savings will be higher.
              </p>

              {/* CTA */}
              <div className="rounded-2xl border border-[#F97316]/20 bg-[#111827] p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <p className="text-[#F1F5F9] font-bold">Ready to validate these numbers for your operation?</p>
                  <p className="text-[#64748B] text-sm mt-1">Talk to our team with your specific flange scope and shutdown schedule.</p>
                </div>
                <Link
                  href="/contact#demo"
                  className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#F97316] text-white font-semibold hover:bg-[#EA580C] transition-all duration-150 shadow-lg shadow-orange-500/20 active:scale-95 text-sm"
                >
                  Request Demo
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
